import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import Select from "react-select";
import "./Style.css";
import { TournamentHeader } from "./GameSessionHeader";
import { TournamentActivityList } from "./TournamentActivityList";
import {
  FaPlus,
  FaPlay,
  FaHistory,
  FaTimes,
  FaCalendarAlt,
  FaClock,
  FaDollarSign,
  FaInfo,
  FaCog,
  FaBell,
  FaCopy,
  FaTrash,
  FaTrophy,
} from "react-icons/fa";

const POINT_MULTIPLIER_OPTIONS = [1, 1.5, 2, 2.5, 3, 5, 10];
const COUNTDOWN_OPTIONS = [
  { label: "None", value: 0 },
  { label: "1 min", value: 60 },
  { label: "5 min", value: 300 },
  { label: "10 min", value: 600 },
  { label: "30 min", value: 1800 },
  { label: "1 hour", value: 3600 },
];

const CUSTOM_ALLOCATION_ACTIONS = [
  "buyin",
  "buyin_addon",
  "bonus",
  "rebuy",
  "rebuy_addon",
  "addon",
];

const createEmptyAllocationAmounts = () =>
  CUSTOM_ALLOCATION_ACTIONS.reduce((acc, action) => {
    acc[action] = 0;
    return acc;
  }, {});

const money = (value) => `$${Math.round(Number(value) || 0).toLocaleString()}`;

const parseCustomAllocations = (value) => {
  if (!value) return [];
  let parsed = value;
  if (typeof value === "string") {
    try {
      parsed = JSON.parse(value);
    } catch (err) {
      return [];
    }
  }
  if (!Array.isArray(parsed)) return [];
  return parsed
    .map((allocation) => ({
      ...allocation,
      total: Number(allocation?.total) || 0,
    }))
    .filter((allocation) => allocation.name);
};

const handleNumberFocus = (e) => {
  if (String(e.target.value) === "0") {
    e.target.value = "";
    return;
  }
  e.target.select();
};

const preventNumberInputWheel = (e) => {
  if (e.target?.tagName === "INPUT" && e.target.type === "number") {
    e.preventDefault();
  }
};

export default function TournamentsHome() {
  const { cardroom } = useParams();
  const navigate = useNavigate();
  const token = localStorage.getItem("token");
  const permission = localStorage.getItem("permission");
  const dealerMode = Number(localStorage.getItem("dealer_mode") || 0);

  // State
  const [tournaments, setTournaments] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [savedTournaments, setSavedTournaments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currSession, setCurrSession] = useState(null);

  // Wallets & Cash
  const [tournamentCash, setTournamentCash] = useState(0);
  const [tournamentWallets, setTournamentWallets] = useState([]);
  const [newWalletName, setNewWalletName] = useState("");
  const [notifications, setNotifications] = useState([]);

  // Cash Bag Additions
  const [refillAmount, setRefillAmount] = useState("");
  const [refillWalletId, setRefillWalletId] = useState("");
  const isValidRefillInt =
    Number.isInteger(Number(refillAmount)) && Number(refillAmount) >= 1;
  const [isCashBagHistoryOpen, setIsCashBagHistoryOpen] = useState(false);
  const [cashBagTransactions, setCashBagTransactions] = useState([]);

  // Session Totals
  const [sessionRake, setSessionRake] = useState(0);
  const [sessionTips, setSessionTips] = useState(0);
  const [sessionCosts, setSessionCosts] = useState(0);

  const [isSessionCostsOpen, setIsSessionCostsOpen] = useState(false);
  const [sessionCostsData, setSessionCostsData] = useState([]);

  const fetchSessionCosts = async () => {
    try {
      const { data } = await axios.get(
        `/${cardroom}/api/tournaments/session_costs`,
      );
      setSessionCostsData(data);
      setIsSessionCostsOpen(true);
    } catch (err) {
      console.error("Fetch session costs failed", err);
    }
  };

  // Quick Start Form
  const [selectedTemplate, setSelectedTemplate] = useState("");
  const [name, setName] = useState("");
  const [buyin, setBuyin] = useState(100);
  const [bonusBuyin, setBonusBuyin] = useState("");
  const [bonusRake, setBonusRake] = useState("");
  const [bonusTips, setBonusTips] = useState("");
  const [rake, setRake] = useState(20);
  const [tips, setTips] = useState("");
  const [buyinAddonCost, setBuyinAddonCost] = useState(0);
  const [buyinAddonRake, setBuyinAddonRake] = useState(0);
  const [buyinAddonTips, setBuyinAddonTips] = useState(0);
  const [rebuyCost, setRebuyCost] = useState("");
  const [rebuyRake, setRebuyRake] = useState(0);
  const [rebuyTips, setRebuyTips] = useState(0);
  const [rebuyAddOnCost, setRebuyAddOnCost] = useState(0);
  const [rebuyAddOnRake, setRebuyAddOnRake] = useState(0);
  const [rebuyAddOnTips, setRebuyAddOnTips] = useState(0);

  // Add-on Fields
  const [addonCost, setAddonCost] = useState(0);
  const [addonRake, setAddonRake] = useState(0);
  const [addonTips, setAddonTips] = useState(0);
  const [customAllocations, setCustomAllocations] = useState([]);
  const [newAllocationName, setNewAllocationName] = useState("");
  const [isAddingAllocation, setIsAddingAllocation] = useState(false);
  const [pointPresets, setPointPresets] = useState([]);
  const [pointsEnabled, setPointsEnabled] = useState(true);
  const [pointsPresetId, setPointsPresetId] = useState("");
  const [pointsMultiplier, setPointsMultiplier] = useState(1);
  const [bountyEnabled, setBountyEnabled] = useState(false);
  const [bountyAmount, setBountyAmount] = useState(0);

  const [startTime, setStartTime] = useState(""); // datetime-local string
  const [countdownSeconds, setCountdownSeconds] = useState(60);

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [createModalMode, setCreateModalMode] = useState("create");
  const [editingSavedTournament, setEditingSavedTournament] = useState(null);
  const [savedTournamentTemplateConfig, setSavedTournamentTemplateConfig] =
    useState(null);
  const [registrationModal, setRegistrationModal] = useState({
    isOpen: false,
    tournament: null,
    loading: false,
  });
  const [registrationPlayers, setRegistrationPlayers] = useState([]);
  const [registeredPlayers, setRegisteredPlayers] = useState([]);
  const [selectedRegistrationPlayers, setSelectedRegistrationPlayers] =
    useState([]);
  const [deleteTournamentModal, setDeleteTournamentModal] = useState({
    isOpen: false,
    tournament: null,
  });
  const [deleteTemplateModal, setDeleteTemplateModal] = useState({
    isOpen: false,
    template: null,
  });
  const [deleteSavedTournamentModal, setDeleteSavedTournamentModal] = useState({
    isOpen: false,
    savedTournament: null,
  });
  const [visiblePastCount, setVisiblePastCount] = useState(5);

  useEffect(() => {
    if (!token) {
      navigate("/");
      return;
    }
    if (dealerMode === 2) {
      navigate(`/${cardroom}/dealerdashboard`);
      return;
    }
    if (!permission?.includes("O") && !permission?.includes("Y")) {
      navigate(`/${cardroom}/game`);
    }
  }, [token, cardroom, navigate, dealerMode, permission]);

  const formatDateTimeLocal = (date) => {
    const pad = (value) => String(value).padStart(2, "0");
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
  };

  const getDefaultTournamentStartTime = () => {
    const date = new Date();
    date.setHours(date.getHours() + 1);
    if (date.getMinutes() || date.getSeconds() || date.getMilliseconds()) {
      date.setHours(date.getHours() + 1, 0, 0, 0);
    } else {
      date.setSeconds(0, 0);
    }
    return formatDateTimeLocal(date);
  };

  const getTemplateConfig = (template) => {
    if (!template) return {};
    const config = template.config_json || {};
    if (typeof config !== "string") return config;
    try {
      return JSON.parse(config);
    } catch (err) {
      return {};
    }
  };

  const getSelectedTemplate = (templateId = selectedTemplate) =>
    templates.find((t) => t.id === Number(templateId));

  const getSelectedTemplateConfig = (templateId = selectedTemplate) =>
    getTemplateConfig(getSelectedTemplate(templateId));

  const getCurrentStructureConfig = () => {
    if (selectedTemplate) return getSelectedTemplateConfig(selectedTemplate);
    return savedTournamentTemplateConfig || {};
  };

  const getTemplateFeatureVisibility = (
    templateId = selectedTemplate,
    fallbackConfig = savedTournamentTemplateConfig,
  ) => {
    const config = templateId ? getSelectedTemplateConfig(templateId) : fallbackConfig || {};
    return {
      showBonus: Number(config.bonusChips || config.bonusStartingChips || 0) > 0,
      showBuyinAddon: Number(config.buyinAddonChips || 0) > 0,
      showRebuyAddon: Number(config.rebuysAddOnChips || 0) > 0,
      showTournamentAddon: Number(config.addonChips || config.addonsChips || 0) > 0,
    };
  };

  const getCustomAllocationAmount = (allocation, action) =>
    Number(allocation?.amounts?.[action]) || 0;

  const getCustomAllocationTotalForAction = (action) =>
    customAllocations.reduce(
      (sum, allocation) => sum + getCustomAllocationAmount(allocation, action),
      0,
    );

const handleAddCustomAllocation = () => {
  const trimmed = newAllocationName.trim();
  if (!trimmed) return;
  const isDuplicate = customAllocations.some(
    (allocation) => allocation.name.trim().toLowerCase() === trimmed.toLowerCase(),
  );
  if (isDuplicate) return;
  setCustomAllocations((prev) => [
    ...prev,
    {
      id: `custom_${Date.now()}_${prev.length + 1}`,
      name: trimmed,
      amounts: createEmptyAllocationAmounts(),
      total: 0,
    },
  ]);
  setNewAllocationName("");
  setIsAddingAllocation(false);
};

  const updateCustomAllocationAmount = (allocationId, action, value) => {
    setCustomAllocations((prev) =>
      prev.map((allocation) =>
        allocation.id === allocationId
          ? {
              ...allocation,
              amounts: {
                ...allocation.amounts,
                [action]: value === "" ? "" : Number(value),
              },
            }
          : allocation,
      ),
    );
  };

  const normalizedCustomAllocations = () =>
    customAllocations.map((allocation) => ({
      ...allocation,
      amounts: CUSTOM_ALLOCATION_ACTIONS.reduce((acc, action) => {
        acc[action] = Number(allocation.amounts?.[action]) || 0;
        return acc;
      }, {}),
      total: Number(allocation.total) || 0,
    }));

  const getSavedTournamentConfig = (savedTournament) => {
    const config = savedTournament?.config_json || {};
    if (typeof config !== "string") return config;
    try {
      return JSON.parse(config);
    } catch (err) {
      return {};
    }
  };

  const getActivePointPreset = () =>
    pointPresets.find((preset) => preset.active) || pointPresets[0];

  const buildSavedTournamentConfig = () => ({
    selectedTemplateId: selectedTemplate ? Number(selectedTemplate) : null,
    templateName: getSelectedTemplate()?.name || "",
    templateConfig: getCurrentStructureConfig(),
    name: name.trim(),
    startTime,
    countdownSeconds: Number(countdownSeconds) || 0,
    buyin,
    bonusBuyin,
    bonusRake,
    bonusTips,
    rake,
    tips,
    buyinAddonCost,
    buyinAddonRake,
    buyinAddonTips,
    rebuyCost,
    rebuyRake,
    rebuyTips,
    rebuyAddOnCost,
    rebuyAddOnRake,
    rebuyAddOnTips,
    addonCost,
    addonRake,
    addonTips,
    customAllocations: normalizedCustomAllocations(),
    pointsEnabled,
    pointsPresetId: pointsPresetId ? Number(pointsPresetId) : null,
    pointsMultiplier: Number(pointsMultiplier) || 1,
    bountyEnabled,
    bountyAmount: Number(bountyAmount) || 0,
  });

  const applySavedTournamentToForm = (savedTournament, mode) => {
    const config = getSavedTournamentConfig(savedTournament);
    const templateId = config.selectedTemplateId ? String(config.selectedTemplateId) : "";
    const templateStillExists = templates.some(
      (template) => Number(template.id) === Number(templateId),
    );
    const activePreset = getActivePointPreset();

    setCreateModalMode(mode);
    setEditingSavedTournament(mode === "editSaved" ? savedTournament : null);
    setSavedTournamentTemplateConfig(config.templateConfig || null);
    setSelectedTemplate(templateStillExists ? templateId : "");
    setName(config.name || savedTournament?.name || "");
    setStartTime(
      mode === "create"
        ? getDefaultTournamentStartTime()
        : config.startTime || getDefaultTournamentStartTime(),
    );
    setCountdownSeconds(Number(config.countdownSeconds ?? 60));
    setBuyin(config.buyin ?? 0);
    setRake(config.rake ?? 0);
    setTips(config.tips ?? 0);
    setBonusBuyin(config.bonusBuyin ?? 0);
    setBonusRake(config.bonusRake ?? 0);
    setBonusTips(config.bonusTips ?? 0);
    setBuyinAddonCost(config.buyinAddonCost ?? 0);
    setBuyinAddonRake(config.buyinAddonRake ?? 0);
    setBuyinAddonTips(config.buyinAddonTips ?? 0);
    setRebuyCost(config.rebuyCost ?? 0);
    setRebuyRake(config.rebuyRake ?? 0);
    setRebuyTips(config.rebuyTips ?? 0);
    setRebuyAddOnCost(config.rebuyAddOnCost ?? 0);
    setRebuyAddOnRake(config.rebuyAddOnRake ?? 0);
    setRebuyAddOnTips(config.rebuyAddOnTips ?? 0);
    setAddonCost(config.addonCost ?? 0);
    setAddonRake(config.addonRake ?? 0);
    setAddonTips(config.addonTips ?? 0);
    setCustomAllocations(parseCustomAllocations(config.customAllocations));
    setPointsEnabled(config.pointsEnabled ?? true);
    setPointsPresetId(
      config.pointsPresetId
        ? String(config.pointsPresetId)
        : activePreset
          ? String(activePreset.id)
          : "",
    );
    setPointsMultiplier(Number(config.pointsMultiplier) || 1);
    setBountyEnabled(Boolean(config.bountyEnabled));
    setBountyAmount(Number(config.bountyAmount) || 0);
    setIsCreateModalOpen(true);
  };

  const handleTemplateChange = (templateId) => {
    setSelectedTemplate(templateId);
    setSavedTournamentTemplateConfig(null);
    const template = getSelectedTemplate(templateId);
    setName((currentName) =>
      currentName.trim() || !template ? currentName : template.name,
    );
    const visibility = getTemplateFeatureVisibility(templateId);
    if (!visibility.showBonus) {
      setBonusBuyin(0);
      setBonusRake(0);
      setBonusTips(0);
    }
    if (!visibility.showBuyinAddon) {
      setBuyinAddonCost(0);
      setBuyinAddonRake(0);
      setBuyinAddonTips(0);
    }
    if (!visibility.showRebuyAddon) {
      setRebuyAddOnCost(0);
      setRebuyAddOnRake(0);
      setRebuyAddOnTips(0);
    }
    if (!visibility.showTournamentAddon) {
      setAddonCost(0);
      setAddonRake(0);
      setAddonTips(0);
    }
  };

  const openCreateModal = () => {
    setCreateModalMode("create");
    setEditingSavedTournament(null);
    setSavedTournamentTemplateConfig(null);
    setStartTime(getDefaultTournamentStartTime());
    setCountdownSeconds(60);
    const template = getSelectedTemplate();
    if (template) setName(template.name);
    const activePreset = pointPresets.find((preset) => preset.active) || pointPresets[0];
    setBuyin(0);
    setRake(0);
    setTips(0);
    setBonusBuyin(0);
    setBonusRake(0);
    setBonusTips(0);
    setBuyinAddonCost(0);
    setBuyinAddonRake(0);
    setBuyinAddonTips(0);
    setRebuyCost(0);
    setRebuyRake(0);
    setRebuyTips(0);
    setRebuyAddOnCost(0);
    setRebuyAddOnRake(0);
    setRebuyAddOnTips(0);
    setAddonCost(0);
    setAddonRake(0);
    setAddonTips(0);
    setCustomAllocations([]);
    setPointsEnabled(true);
    setPointsPresetId(activePreset ? String(activePreset.id) : "");
    setPointsMultiplier(1);
    setBountyEnabled(false);
    setBountyAmount(0);
    setIsCreateModalOpen(true);
  };

  // Modals for Wallets
  const [payingWallet, setPayingWallet] = useState(null);
  const [payDirection, setPayDirection] = useState("to_wallet");
  const [payWalletAmount, setPayWalletAmount] = useState("");
  const [payWalletNotes, setPayWalletNotes] = useState("");

  const [selectedWalletId, setSelectedWalletId] = useState(null);
  const [selectedWalletTransactions, setSelectedWalletTransactions] = useState(
    [],
  );

  // Auth Effect
  useEffect(() => {
    if (!token) {
      navigate("/");
      return;
    }

    const interceptor = axios.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 401) {
          localStorage.removeItem("token");
          navigate(`/${cardroom}/`);
        }
        return Promise.reject(error);
      },
    );

    return () => axios.interceptors.response.eject(interceptor);
  }, [token, dealerMode, cardroom, navigate]);

  const loadData = async () => {
    try {
      const [tourneyRes, templateRes, savedRes, cardroomRes, walletsRes, pointRes] =
        await Promise.all([
          axios.get(`/${cardroom}/api/tournaments`),
          axios.get(`/${cardroom}/api/tournaments/templates`),
          axios.get(`/${cardroom}/api/tournaments/saved`),
          axios.get(`/${cardroom}/api/cardroom`),
          axios.get(`/${cardroom}/api/tournament_wallets`),
          axios.get(`/${cardroom}/api/tournaments/points/settings`),
        ]);
      setTournaments(tourneyRes.data);
      setTemplates(templateRes.data);
      setSavedTournaments(savedRes.data);
      setPointPresets(pointRes.data.presets || []);
      const activePreset = (pointRes.data.presets || []).find((preset) => preset.active) || (pointRes.data.presets || [])[0];
      if (!pointsPresetId && activePreset) setPointsPresetId(String(activePreset.id));
      setCurrSession(cardroomRes.data.active);
      setTournamentCash(cardroomRes.data.tournamentCash || 0);
      setTournamentWallets(walletsRes.data);

      const currentSessionId = cardroomRes.data.lastSessionId;
      let sRake = 0,
        sTips = 0,
        sCosts = 0;
      if (currentSessionId) {
        tourneyRes.data.forEach((t) => {
          if (Number(t.session_id) === Number(currentSessionId)) {
            sRake += Number(t.total_rake) || 0;
            sTips += Number(t.total_tips) || 0;
            sCosts += Number(t.costs) || 0;
          }
        });
      }
      setSessionRake(sRake);
      setSessionTips(sTips);
      setSessionCosts(sCosts);
    } catch (err) {
      console.error("Failed to load tournaments", err);
    } finally {
      setLoading(false);
    }
  };

  // Load Data
  useEffect(() => {
    if (!token) return;
    axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
    loadData();
  }, [token, cardroom]);

  const buildTournamentPayload = () => {
    const config = { ...getCurrentStructureConfig() };

    if (startTime) {
      config.scheduledStartTime = startTime;
    }
    config.countdownSeconds = Number(countdownSeconds) || 0;
    const featureVisibility = getTemplateFeatureVisibility(selectedTemplate, config);

    return {
      name,
      buyin: Number(buyin) || 0,
      bonusBuyin: featureVisibility.showBonus ? Number(bonusBuyin) || 0 : 0,
      bonusRake: featureVisibility.showBonus ? Number(bonusRake) || 0 : 0,
      bonusTips: featureVisibility.showBonus ? Number(bonusTips) || 0 : 0,
      rake: Number(rake) || 0,
      tips: Number(tips) || 0,
      buyinAddonCost: featureVisibility.showBuyinAddon ? Number(buyinAddonCost) || 0 : 0,
      buyinAddonRake: featureVisibility.showBuyinAddon ? Number(buyinAddonRake) || 0 : 0,
      buyinAddonTips: featureVisibility.showBuyinAddon ? Number(buyinAddonTips) || 0 : 0,
      rebuysCost: Number(rebuyCost),
      rebuysRake: Number(rebuyRake),
      rebuysTips: Number(rebuyTips),

      addonsCost: featureVisibility.showTournamentAddon ? Number(addonCost) : 0,
      addonsRake: featureVisibility.showTournamentAddon ? Number(addonRake) : 0,
      addonsTips: featureVisibility.showTournamentAddon ? Number(addonTips) : 0,

      rebuysAddOnCosts: featureVisibility.showRebuyAddon ? Number(rebuyAddOnCost) : 0,
      rebuysAddOnRake: featureVisibility.showRebuyAddon ? Number(rebuyAddOnRake) : 0,
      rebuysAddOnTips: featureVisibility.showRebuyAddon ? Number(rebuyAddOnTips) : 0,

      startingChips: Number(config.startingChips || 0),
      bonusStartingChips: Number(
        config.bonusChips || config.bonusStartingChips || 0,
      ),
      buyinAddonChips: Number(config.buyinAddonChips || 0),
      rebuysChips: Number(config.rebuysChips || 0),
      rebuysCutoffLevel: Number(
        config.rebuyCutoffLevel || config.rebuysCutoffLevel || 0,
      ),
      addonsChips: Number(config.addonChips || config.addonsChips || 0),
      rebuysAddOnChips: Number(config.rebuysAddOnChips || 0),
      pointsEnabled,
      pointsPresetId: pointsEnabled ? Number(pointsPresetId) : null,
      pointsMultiplier: Number(pointsMultiplier) || 1,
      bountyEnabled,
      bountyAmount: bountyEnabled ? Number(bountyAmount) || 0 : 0,
      customAllocations: normalizedCustomAllocations(),

      config,
    };
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!isFormValid() || isSubmitting) return;

    setIsSubmitting(true);
    try {
      if (createModalMode === "save" || createModalMode === "editSaved") {
        await saveTournamentSetup();
        return;
      }

      await axios.post(`/${cardroom}/api/tournaments`, buildTournamentPayload());

      // If start time is in the future, it just stays in "registering" state (Upcoming)
      // If no start time (or immediate), user might want to go manage it immediately?
      // User requested logic: "user can start them by pressing start moving it into active"
      // So all tournaments created here are basically "Upcoming" until manually started or managed.

      // Reload tournaments
      const { data: freshTournaments } = await axios.get(
        `/${cardroom}/api/tournaments`,
      );
      setTournaments(freshTournaments);

      // Reset form
      setName("");
      setStartTime("");
      setCountdownSeconds(60);
      // keep financial settings as defaults often repeat, but maybe reset name

      setIsCreateModalOpen(false);
    } catch (err) {
      console.error("Create failed", err);
      alert("Failed to create tournament");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDuplicate = async (templateId) => {
    try {
      const { data } = await axios.post(
        `/${cardroom}/api/tournaments/templates/${templateId}/duplicate`,
      );
      setTemplates((prev) => [
        ...prev,
        {
          ...data,
          config_json:
            typeof data.config_json === "string"
              ? JSON.parse(data.config_json)
              : data.config_json,
        },
      ]);
    } catch (err) {
      console.error("Duplicate failed", err);
      alert("Failed to duplicate template");
    }
  };

  const handleDuplicateSavedTournament = async (savedTournamentId) => {
    try {
      const { data } = await axios.post(
        `/${cardroom}/api/tournaments/saved/${savedTournamentId}/duplicate`,
      );
      setSavedTournaments((prev) => [...prev, data]);
    } catch (err) {
      console.error("Duplicate saved tournament failed", err);
      alert("Failed to duplicate saved tournament");
    }
  };

  const handleStartNow = async (tournamentId) => {
    try {
      // If the tournament is just registering, set it to "running" (or active state)
      // We will use the 'control' endpoint to start it if needed, or update state?
      // Actually, let's just use the control endpoint to 'start', which sets state to running and sets timer_start_at

      await axios.post(`/${cardroom}/api/tournaments/${tournamentId}/control`, {
        action: "activate",
      });

      // Refresh list
      const { data: freshTournaments } = await axios.get(
        `/${cardroom}/api/tournaments`,
      );
      setTournaments(freshTournaments);
    } catch (err) {
      console.error(err);
    }
  };

  const selectStyles = {
    control: (base) => ({
      ...base,
      backgroundColor: "#2d3748",
      borderColor: "#4a5568",
      color: "#fff",
    }),
    menu: (base) => ({
      ...base,
      backgroundColor: "#2d3748",
      zIndex: 2000,
    }),
    option: (base, { isFocused }) => ({
      ...base,
      backgroundColor: isFocused ? "#4a5568" : "#2d3748",
      color: "#e2e8f0",
    }),
    multiValue: (base) => ({
      ...base,
      backgroundColor: "#4a5568",
    }),
    multiValueLabel: (base) => ({
      ...base,
      color: "#e2e8f0",
    }),
    input: (base) => ({
      ...base,
      color: "#e2e8f0",
    }),
  };

  const loadRegistrationModalData = async (tournamentId) => {
    const [{ data: tournamentData }, { data: playersData }] = await Promise.all(
      [
        axios.get(`/${cardroom}/api/tournaments/${tournamentId}`),
        axios.get(`/${cardroom}/api/players`),
      ],
    );
    setRegistrationModal({
      isOpen: true,
      tournament: tournamentData,
      loading: false,
    });
    setRegistrationPlayers(playersData);
    setRegisteredPlayers(
      (tournamentData.players || []).filter((p) => p.status === "registered"),
    );
    setSelectedRegistrationPlayers([]);
  };

  const openRegistrationModal = async (tournament) => {
    setRegistrationModal({ isOpen: true, tournament, loading: true });
    setRegistrationPlayers([]);
    setRegisteredPlayers([]);
    setSelectedRegistrationPlayers([]);
    try {
      await loadRegistrationModalData(tournament.id);
    } catch (err) {
      console.error("Load tournament registration failed", err);
      alert("Failed to load tournament registration");
      setRegistrationModal({ isOpen: false, tournament: null, loading: false });
    }
  };

  const confirmBulkRegisterPlayers = async () => {
    if (
      !registrationModal.tournament ||
      selectedRegistrationPlayers.length === 0 ||
      isSubmitting
    )
      return;
    setIsSubmitting(true);
    try {
      await Promise.all(
        selectedRegistrationPlayers.map((p) =>
          axios.post(
            `/${cardroom}/api/tournaments/${registrationModal.tournament.id}/register`,
            { playerId: p.value },
          ),
        ),
      );
      await loadRegistrationModalData(registrationModal.tournament.id);
      await loadData();
    } catch (err) {
      console.error("Register players failed", err);
      alert("Failed to register players");
    } finally {
      setIsSubmitting(false);
    }
  };

  const removeRegisteredPlayer = async (playerId) => {
    if (!registrationModal.tournament || isSubmitting) return;
    setIsSubmitting(true);
    try {
      await axios.delete(
        `/${cardroom}/api/tournaments/${registrationModal.tournament.id}/register/${playerId}`,
      );
      await loadRegistrationModalData(registrationModal.tournament.id);
      await loadData();
    } catch (err) {
      console.error("Unregister player failed", err);
      alert("Failed to remove registered player");
    } finally {
      setIsSubmitting(false);
    }
  };

  const confirmDeleteTournament = async () => {
    if (!deleteTournamentModal.tournament || isSubmitting) return;
    setIsSubmitting(true);
    try {
      await axios.delete(
        `/${cardroom}/api/tournaments/${deleteTournamentModal.tournament.id}`,
      );
      setDeleteTournamentModal({ isOpen: false, tournament: null });
      if (
        registrationModal.tournament?.id === deleteTournamentModal.tournament.id
      ) {
        setRegistrationModal({
          isOpen: false,
          tournament: null,
          loading: false,
        });
      }
      await loadData();
    } catch (err) {
      console.error("Delete tournament failed", err);
      alert(err.response?.data?.error || "Failed to delete tournament");
    } finally {
      setIsSubmitting(false);
    }
  };

  const confirmDeleteTemplate = async () => {
    if (!deleteTemplateModal.template || isSubmitting) return;
    setIsSubmitting(true);
    try {
      await axios.delete(
        `/${cardroom}/api/tournaments/templates/${deleteTemplateModal.template.id}`,
      );
      setTemplates((prev) =>
        prev.filter((t) => t.id !== deleteTemplateModal.template.id),
      );
      if (
        Number(selectedTemplate) === Number(deleteTemplateModal.template.id)
      ) {
        setSelectedTemplate("");
        setName("");
      }
      setDeleteTemplateModal({ isOpen: false, template: null });
    } catch (err) {
      console.error("Delete template failed", err);
      alert(err.response?.data?.error || "Failed to delete template");
    } finally {
      setIsSubmitting(false);
    }
  };

  const saveTournamentSetup = async () => {
    const savedConfig = buildSavedTournamentConfig();
    const payload = {
      name: name.trim(),
      config: savedConfig,
    };

    if (createModalMode === "editSaved" && editingSavedTournament) {
      const { data } = await axios.put(
        `/${cardroom}/api/tournaments/saved/${editingSavedTournament.id}`,
        payload,
      );
      setSavedTournaments((prev) =>
        prev.map((saved) =>
          Number(saved.id) === Number(editingSavedTournament.id) ? data : saved,
        ),
      );
    } else {
      const { data } = await axios.post(
        `/${cardroom}/api/tournaments/saved`,
        payload,
      );
      setSavedTournaments((prev) => [...prev, data]);
    }

    setIsCreateModalOpen(false);
    setCreateModalMode("create");
    setEditingSavedTournament(null);
  };

  const handleSaveTournamentSetup = async () => {
    if (!isFormValid() || isSubmitting) return;

    setIsSubmitting(true);
    try {
      await saveTournamentSetup();
    } catch (err) {
      console.error("Save tournament failed", err);
      alert(err.response?.data?.error || "Failed to save tournament");
    } finally {
      setIsSubmitting(false);
    }
  };

  const confirmDeleteSavedTournament = async () => {
    if (!deleteSavedTournamentModal.savedTournament || isSubmitting) return;

    setIsSubmitting(true);
    try {
      await axios.delete(
        `/${cardroom}/api/tournaments/saved/${deleteSavedTournamentModal.savedTournament.id}`,
      );
      setSavedTournaments((prev) =>
        prev.filter(
          (saved) => saved.id !== deleteSavedTournamentModal.savedTournament.id,
        ),
      );
      if (
        editingSavedTournament &&
        Number(editingSavedTournament.id) ===
          Number(deleteSavedTournamentModal.savedTournament.id)
      ) {
        setEditingSavedTournament(null);
        setCreateModalMode("create");
      }
      setDeleteSavedTournamentModal({ isOpen: false, savedTournament: null });
    } catch (err) {
      console.error("Delete saved tournament failed", err);
      alert(err.response?.data?.error || "Failed to delete saved tournament");
    } finally {
      setIsSubmitting(false);
    }
  };

  const isFormValid = () => {
    const {
      showBonus,
      showBuyinAddon,
      showRebuyAddon,
      showTournamentAddon,
    } = getTemplateFeatureVisibility();
    const requiredMoneyFields = [
      buyin,
      rake,
      tips,
      rebuyCost,
      rebuyRake,
      rebuyTips,
    ];
    if (showBonus) requiredMoneyFields.push(bonusBuyin, bonusRake, bonusTips);
    if (showBuyinAddon) requiredMoneyFields.push(buyinAddonCost, buyinAddonRake, buyinAddonTips);
    if (showRebuyAddon) requiredMoneyFields.push(rebuyAddOnCost, rebuyAddOnRake, rebuyAddOnTips);
    if (showTournamentAddon) requiredMoneyFields.push(addonCost, addonRake, addonTips);
    return (
      name.trim() &&
      (selectedTemplate || Object.keys(savedTournamentTemplateConfig || {}).length > 0) &&
      startTime !== "" &&
      requiredMoneyFields.every(
        (v) => v !== "" && v !== null && v !== undefined,
      ) &&
      (!pointsEnabled || Boolean(pointsPresetId)) &&
      (!bountyEnabled || Number(bountyAmount) > 0)
    );
  };

  const clearZeroOnFocus = (value, setter) => {
    if (String(value) === "0") setter("");
  };

  const restoreZeroOnBlur = (value, setter) => {
    if (value === "" || value === null || value === undefined) setter(0);
  };

  const toggleTournamentPoints = (enabled) => {
    setPointsEnabled(enabled);
    if (enabled && !pointsPresetId) {
      const activePreset =
        pointPresets.find((preset) => preset.active) || pointPresets[0];
      if (activePreset) setPointsPresetId(String(activePreset.id));
    }
  };

  const handleAddWallet = async (e) => {
    e.preventDefault();
    if (isSubmitting || !newWalletName.trim()) return;

    try {
      setIsSubmitting(true);
      const { data: wallet } = await axios.post(
        `/${cardroom}/api/tournament_wallets`,
        {
          name: newWalletName.trim(),
        },
      );

      setTournamentWallets((prev) => [...prev, wallet]);
      setNewWalletName("");
    } catch (err) {
      alert(err.response?.data?.error || "Could not add wallet");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleWalletPayment = async (e) => {
    e.preventDefault();
    if (isSubmitting) return;

    try {
      setIsSubmitting(true);
      const actualAmount =
        payDirection === "to_wallet"
          ? parseFloat(payWalletAmount)
          : -parseFloat(payWalletAmount);

      await axios.post(`/${cardroom}/api/wallet_payment`, {
        walletId: payingWallet.id,
        amount: actualAmount,
        notes: payWalletNotes.trim(),
        tournament: 1,
      });

      setTournamentWallets((prev) =>
        prev.map((w) =>
          w.id === payingWallet.id
            ? { ...w, digital: w.digital + actualAmount }
            : w,
        ),
      );

      setPayingWallet(null);
      setPayDirection("to_wallet");
      setPayWalletAmount("");
      setPayWalletNotes("");
    } catch (err) {
      console.error("Wallet payment failed", err);
      alert(err.response?.data?.error || "Could not process payment");
    } finally {
      setIsSubmitting(false);
    }
  };

  const listWalletTransactions = async (walletId) => {
    setSelectedWalletId(walletId);
    try {
      const { data } = await axios.get(
        `/${cardroom}/api/tournament_wallets/${walletId}/transactions`,
      );
      setSelectedWalletTransactions(data);
    } catch (err) {
      console.error("List wallet transactions failed", err);
    }
  };

  const listCashBagTransactions = async () => {
    try {
      const { data } = await axios.get(
        `/${cardroom}/api/tournaments/cashbag_transactions`,
      );
      setCashBagTransactions(data);
      setIsCashBagHistoryOpen(true);
    } catch (err) {
      console.error("List cash bag trans failed", err);
    }
  };

  const verifyCashAmount = async () => {
    try {
      await axios.post(`/${cardroom}/api/tournaments/cash_verify`);
      setNotifications((prev) => [
        ...prev,
        { id: Date.now(), message: `Cash bag verified at $${tournamentCash}` },
      ]);
    } catch (err) {
      console.error("Verification failed", err);
    }
  };

  const clearCashBag = async () => {
    try {
      await axios.post(`/${cardroom}/api/tournaments/cash_reset`);
      setTournamentCash(0);
      setNotifications((prev) => [
        ...prev,
        { id: Date.now(), message: "Cash bag cleared" },
      ]);
    } catch (err) {
      console.error("Clear failed", err);
    }
  };

  const addCashRefill = async (e) => {
    e.preventDefault();
    if (isSubmitting || !isValidRefillInt || !refillWalletId) return;

    try {
      setIsSubmitting(true);
      await axios.post(
        `/${cardroom}/api/tournaments/${refillWalletId}/cash_refill`,
        {
          amount: Number(refillAmount),
        },
      );

      setRefillAmount("");
      setRefillWalletId("");

      setTournamentCash((prev) => prev + Number(refillAmount));
      setTournamentWallets((prev) =>
        prev.map((w) =>
          w.id === Number(refillWalletId)
            ? { ...w, digital: w.digital - Number(refillAmount) }
            : w,
        ),
      );
    } catch (err) {
      console.error("Refill failed", err);
    } finally {
      setIsSubmitting(false);
    }
  };

  const totalWalletBalance = tournamentWallets.reduce(
    (sum, w) => sum + (w.digital || 0),
    0,
  );
  const isWalletNameTaken = tournamentWallets.some(
    (w) => w.name.trim().toLowerCase() === newWalletName.trim().toLowerCase(),
  );

  // Sections Logic
  // Upcoming: state='registering'.
  // Active: state='running' or 'paused'.
  // Past: state='finished'.

  const upcomingTournaments = tournaments.filter(
    (t) => t.state === "registering",
  );
  const runningTournaments = tournaments.filter(
    (t) => t.state === "running" || t.state === "paused",
  );
  const pastTournaments = tournaments.filter((t) => t.state === "finished");
  const visiblePastTournaments = pastTournaments.slice(0, visiblePastCount);

  // History Modal State
  const [historyTournament, setHistoryTournament] = useState(null);
  const [historyTransactions, setHistoryTransactions] = useState([]);
  const [historyTab, setHistoryTab] = useState("placement");

  const handleOpenHistory = async (t) => {
    setHistoryTournament(t);
    setHistoryTransactions([]);
    setHistoryTab("placement");
    try {
      // We need to fetch transactions. We can use the single tournament endpoint or a dedicated one.
      // The single endpoint returns transactions.
      const { data } = await axios.get(`/${cardroom}/api/tournaments/${t.id}`);
      setHistoryTournament(data);
      setHistoryTransactions(data.transactions || []);
    } catch (err) {
      console.error("Failed to load history", err);
    }
  };

  const historyPlacements = (historyTournament?.player_records || [])
    .filter((record) => record.place !== null && record.place !== undefined)
    .sort((a, b) => (Number(a.place) || 999999) - (Number(b.place) || 999999));
  const historyCustomFees = parseCustomAllocations(historyTournament?.custom_allocations_json);
  const historyCosts = historyTournament?.costs_history || [];

  // ============================================================================
  // MAIN RENDER
  // ============================================================================

  return (
    <div className="game" onWheelCapture={preventNumberInputWheel}>
      <TournamentHeader
        activePage="tournaments"
        cardroom={cardroom}
        currSession={currSession}
        title="Tournaments"
      />

      {/* Notifications */}
      {notifications.length > 0 && (
        <div className="game-notifications">
          {notifications.map((n) => (
            <div key={n.id} className="game-notification">
              <FaBell className="game-notification__icon" />
              <span className="game-notification__message">{n.message}</span>
              <button
                className="game-notification__close"
                onClick={() =>
                  setNotifications((prev) => prev.filter((x) => x.id !== n.id))
                }
              >
                <FaTimes />
              </button>
            </div>
          ))}
        </div>
      )}

      <div className="game-container">
        {loading ? (
          <div
            className="loading-container"
            style={{ padding: "4rem", textAlign: "center", color: "white" }}
          >
            <h2 style={{ marginBottom: "1.5rem", fontWeight: "700" }}>
              Gathering Data...
            </h2>
            <div style={{ display: "flex", justifyContent: "center" }}>
              <div
                className="spinner"
                style={{
                  border: "4px solid rgba(255,255,255,0.1)",
                  borderTop: "4px solid #6366f1",
                  borderRadius: "50%",
                  width: "50px",
                  height: "50px",
                  animation: "spin 1s linear infinite",
                }}
              ></div>
            </div>
            <style>{`@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }`}</style>
          </div>
        ) : (
          <>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr 1fr",
                gap: "2rem",
              }}
            >


              {/* COLUMN 2: Create Tournament & Templates */}
              <div
                style={{
                  background: "var(--game-bg-secondary)",
                  padding: "1.5rem",
                  borderRadius: "var(--game-radius)",
                }}
              >
                <h2 className="game-section__title">Create Tournament</h2>
                <div
                  style={{
                    display: "flex",
                    gap: "1rem",
                    flexDirection: "column",
                  }}
                >
                  <button
                    className="game-icon-btn game-icon-btn--info"
                    style={{
                      padding: "1.5rem",
                      gap: "0.5rem",
                      fontSize: "1.2rem",
                      justifyContent: "center",
                    }}
                    onClick={openCreateModal}
                  >
                    <FaPlus /> Create Tournament
                  </button>
                  <button
                    className="game-icon-btn game-icon-btn--success"
                    style={{
                      padding: "1.5rem",
                      gap: "0.5rem",
                      fontSize: "1.2rem",
                      justifyContent: "center",
                    }}
                    onClick={() => navigate(`/${cardroom}/tournamentsCreate`)}
                  >
                    <FaPlus /> Create Template
                  </button>
                </div>

                {savedTournaments.length > 0 && (
                <div style={{ marginTop: "2rem" }}>
                  <h4
                    style={{
                      color: "var(--game-text-secondary)",
                      marginBottom: "1rem",
                    }}
                  >
                    Saved Tournaments
                  </h4>
                  <div className="game-list">
                    {savedTournaments.length === 0 ? (
                      <p
                        className="game-empty"
                        style={{ padding: "1rem 0", margin: 0 }}
                      >
                        No saved tournaments found.
                      </p>
                    ) : (
                      savedTournaments.map((savedTournament) => (
                        <div
                          key={savedTournament.id}
                          className="game-list-item"
                          style={{
                            display: "grid",
                            gridTemplateColumns: "auto minmax(0, 1fr) repeat(3, auto)",
                            gap: "0.5rem",
                            alignItems: "center",
                          }}
                        >
                          <button
                            className="game-icon-btn game-icon-btn--danger"
                            onClick={(e) => {
                              e.stopPropagation();
                              setDeleteSavedTournamentModal({
                                isOpen: true,
                                savedTournament,
                              });
                            }}
                            title="Delete Saved Tournament"
                          >
                            <FaTrash />
                          </button>
                          <span
                            className="game-list-item__title"
                            style={{
                              margin: 0,
                              fontSize: "1rem",
                              minWidth: 0,
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {savedTournament.name}
                          </span>
                          <button
                            className="game-icon-btn game-icon-btn--warning"
                            onClick={(e) => {
                              e.stopPropagation();
                              applySavedTournamentToForm(savedTournament, "editSaved");
                            }}
                            title="Edit Saved Tournament"
                          >
                            <FaCog />
                          </button>
                          <button
                            className="game-icon-btn game-icon-btn--primary"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDuplicateSavedTournament(savedTournament.id);
                            }}
                            title="Copy Saved Tournament"
                          >
                            <FaCopy />
                          </button>
                                                    <button
                            className="game-icon-btn game-icon-btn--success"
                            onClick={(e) => {
                              e.stopPropagation();
                              applySavedTournamentToForm(savedTournament, "create");
                            }}
                            title="Start Saved Tournament"
                          >
                            <FaPlay />
                          </button>
                        </div>
                      ))
                    )}
                  </div>
                </div>
                )}

                <div style={{ marginTop: "2rem" }}>
                  <h4
                    style={{
                      color: "var(--game-text-secondary)",
                      marginBottom: "1rem",
                    }}
                  >
                    Templates
                  </h4>
                  <div className="game-list">
                    {templates.length === 0 ? (
                      <p
                        className="game-empty"
                        style={{ padding: "1rem 0", margin: 0 }}
                      >
                        No templates found.
                      </p>
                    ) : (
                      templates.map((t) => (
                        <div
                          key={t.id}
                          className="game-list-item"
                          style={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                          }}
                        >
                          <button
                            className="game-icon-btn game-icon-btn--danger"
                            onClick={(e) => {
                              e.stopPropagation();
                              setDeleteTemplateModal({
                                isOpen: true,
                                template: t,
                              });
                            }}
                            title="Delete Template"
                          >
                            <FaTrash />
                          </button>
                          <span
                            className="game-list-item__title"
                            style={{ margin: 0, fontSize: "1rem", flex: 1 }}
                          >
                            {t.name}
                          </span>
                          <button
                            className="game-icon-btn game-icon-btn--warning"
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(
                                `/${cardroom}/tournamentsCreate?id=${t.id}`,
                              );
                            }}
                            title="Edit Template"
                          >
                            <FaCog />
                          </button>
                          <button
                            className="game-icon-btn game-icon-btn--primary"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDuplicate(t.id);
                            }}
                            title="Duplicate Template"
                          >
                            <FaCopy />
                          </button>
                          <button
                            className="game-icon-btn game-icon-btn--info"
                            onClick={(e) => {
                              e.stopPropagation();
                              window.open(
                                `/#/${cardroom}/tournamentsCreate?id=${t.id}&view=true`,
                                "_blank",
                              );
                            }}
                            title="View Template"
                          >
                            <FaInfo />
                          </button>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              </div>

              {/* COLUMN 3: Active, Upcoming & History */}
              <div>
                <h2 className="game-section__title">
                  <FaPlay /> Live
                </h2>
                {runningTournaments.length === 0 ? (
                  <p className="game-text-secondary">No running tournaments.</p>
                ) : (
                  <div className="game-list">
                    {runningTournaments.map((t) => (
                      <div
                        key={t.id}
                        className="tournament-card tournament-card--active"
                      >
                        <div className="tournament-card__main">
                          <div className="tournament-card__title">{t.name}</div>
                          <div className="tournament-card__subtitle">
                            {Number(t.current_level || 0) === 0 ? "Countdown" : `Level ${t.current_level || 1}`} • Entries:{" "}
                            {t.total_entries || 0}
                          </div>
                        </div>
                        <div className="tournament-card__actions">
                          <button
                            className="game-btn game-btn--sm"
                            onClick={() =>
                              window.open(
                                `/#/${cardroom}/tournaments/${t.id}/manage`,
                                "_blank",
                              )
                            }
                          >
                            Manage
                          </button>
                          <button
                            className="game-btn game-btn--sm game-btn--secondary"
                            onClick={() =>
                              window.open(
                                `/#/${cardroom}/tournaments/${t.id}/display`,
                                "_blank",
                              )
                            }
                          >
                            Display
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                <h2
                  className="game-section__title"
                  style={{ marginTop: "2rem" }}
                >
                  <FaCalendarAlt /> Upcoming
                </h2>
                {upcomingTournaments.length === 0 ? (
                  <p className="game-text-secondary">
                    No upcoming tournaments.
                  </p>
                ) : (
                  <div className="game-list">
                    {upcomingTournaments.map((t) => {
                      const scheduled =
                        t.config_json && t.config_json.scheduledStartTime
                          ? new Date(t.config_json.scheduledStartTime)
                          : null;
                      return (
                        <div
                          key={t.id}
                          className="tournament-card tournament-card--upcoming"
                          onClick={() => openRegistrationModal(t)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter" || e.key === " ") {
                              e.preventDefault();
                              openRegistrationModal(t);
                            }
                          }}
                          role="button"
                          tabIndex={0}
                        >
                          <div className="tournament-card__header">
                            <div className="tournament-card__title">{t.name}</div>
                            <div className="tournament-card__date">
                              {scheduled
                                ? scheduled.toLocaleString(undefined, {
                                    weekday: "short",
                                    month: "short",
                                    day: "numeric",
                                    hour: "numeric",
                                    minute: "2-digit",
                                  })
                                : "No Schedule"}
                            </div>
                          </div>
                          <div className="tournament-card__meta-row">
                            <span className="tournament-card__pill">
                              Registered: <strong>{t.registered_count || 0}</strong>
                            </span>
                          </div>
                          <div className="tournament-card__footer">
                            <div className="tournament-card__actions">
                              <button
                                className="game-btn game-btn--danger game-btn--sm"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setDeleteTournamentModal({
                                    isOpen: true,
                                    tournament: t,
                                  });
                                }}
                              >
                                Cancel
                              </button>
                              <button
                                className="game-btn game-btn--success game-btn--sm"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleStartNow(t.id);
                                }}
                              >
                                <FaPlay /> Start
                              </button>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}

                <h2
                  className="game-section__title"
                  style={{ marginTop: "2rem" }}
                >
                  <FaHistory /> Past Tournaments
                </h2>
                <div className="game-list">
                  {pastTournaments.length === 0 && (
                    <p className="game-text-secondary">No history.</p>
                  )}
                  {visiblePastTournaments.map((t) => (
                    <div
                      key={t.id}
                      className="tournament-card tournament-card--past"
                      onClick={() => handleOpenHistory(t)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          handleOpenHistory(t);
                        }
                      }}
                      role="button"
                      tabIndex={0}
                    >
                      <div className="tournament-card__main">
                        <div className="tournament-card__title">
                          {t.name}
                        </div>
                        <div className="tournament-card__subtitle">
                          {new Date(t.created_at).toLocaleDateString()} &bull; Entries:{" "}
                          {t.total_entries || 0}
                        </div>
                      </div>
                    </div>
                  ))}
                  {pastTournaments.length > visiblePastCount && (
                    <button
                      className="game-btn game-btn--secondary"
                      type="button"
                      onClick={() => setVisiblePastCount((count) => count + 5)}
                      style={{ width: "100%", justifyContent: "center" }}
                    >
                      Load 5 More
                    </button>
                  )}
                </div>
              </div>
                            {/* COLUMN 1: Wallets */}
              <section className="game-section">
                <h2 className="game-section__title">Tournament Wallets</h2>

                <div className="game-form" style={{ marginBottom: "1rem" }}>
                  <form
                    onSubmit={handleAddWallet}
                    style={{ display: "flex", gap: "0.5rem", width: "100%" }}
                  >
                    <input
                      type="text"
                      className="game-input"
                      placeholder="Wallet name"
                      value={newWalletName}
                      onChange={(e) => setNewWalletName(e.target.value)}
                      style={{ minWidth: 0, flex: 1 }}
                      required
                    />
                    <button
                      type="submit"
                      className="game-btn game-btn--primary"
                      disabled={
                        isSubmitting ||
                        isWalletNameTaken ||
                        newWalletName.trim() === ""
                      }
                    >
                      {isWalletNameTaken ? "Name Taken" : "Add"}
                    </button>
                  </form>
                </div>

                {totalWalletBalance !== 0 && (
                  <div
                    style={{
                      marginBottom: "1rem",
                      padding: "0.75rem 1rem",
                      background: "var(--game-bg-tertiary)",
                      borderRadius: "var(--game-radius)",
                      border: "1px solid var(--game-border)",
                    }}
                  >
                    <span
                      className="game-text-secondary"
                      style={{ marginRight: "0.5rem" }}
                    >
                      {totalWalletBalance > 0
                        ? "Total Owed from Wallets:"
                        : "Total Owed to Wallets:"}
                    </span>
                    <span style={{ fontSize: "1.125rem", fontWeight: 700 }}>
                      ${Math.abs(totalWalletBalance)}
                    </span>
                  </div>
                )}

                {tournamentWallets.length === 0 ? (
                  <p className="game-empty">No wallets added yet</p>
                ) : (
                  <ul className="game-list">
                    {tournamentWallets.map((w) => {
                      const total = w.digital || 0;

                      return (
                        <li key={w.id} className="game-list-item">
                          <span
                            className="game-list-name"
                            onClick={() => listWalletTransactions(w.id)}
                            style={{ cursor: "pointer" }}
                          >
                            {w.name}
                          </span>

                          {total !== 0 && (
                            <span
                              className={
                                total > 0
                                  ? "game-player-badge game-player-badge--debt"
                                  : "game-player-badge game-player-badge--credit"
                              }
                              style={{ fontSize: "0.9rem" }}
                            >
                              {total > 0
                                ? `Owes $${Math.abs(total)}`
                                : `Owed $${Math.abs(total)}`}
                            </span>
                          )}

                          <div
                            className="game-player-actions"
                            style={{ marginLeft: "auto" }}
                          >
                            <button
                              className="game-icon-btn game-icon-btn--info"
                              onClick={() => listWalletTransactions(w.id)}
                              title="Wallet History"
                            >
                              <FaInfo />
                            </button>

                            <button
                              className="game-icon-btn game-icon-btn--warning"
                              onClick={() => {
                                setPayingWallet(w);
                                setPayDirection(
                                  total > 0 ? "from_wallet" : "to_wallet",
                                );
                                setPayWalletAmount("");
                                setPayWalletNotes("");
                              }}
                              title="Make Payment"
                            >
                              <FaDollarSign />
                            </button>
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </section>
            </div>
          </>
        )}
      </div>

      {/* ============================================================================ */}
      {/* TOURNAMENT WALLETS & CREATE MODALS */}
      {/* ============================================================================ */}

      {/* Create Tournament Modal */}
      {isCreateModalOpen && (
        <>
          <div
            className="game-modal-overlay"
            onClick={() => setIsCreateModalOpen(false)}
          />
          <div
            className="game-modal game-modal--large"
            style={{
              maxWidth: "760px",
              display: "flex",
              flexDirection: "column",
              maxHeight: "85vh",
              overflow: "hidden",
            }}
          >
            <div className="game-modal__header">
              <h3 className="game-modal__title">
                {createModalMode === "editSaved"
                  ? "Edit Saved Tournament"
                  : createModalMode === "save"
                    ? "Save Tournament"
                    : "Create Tournament"}
              </h3>
              <button
                className="game-icon-btn"
                onClick={() => setIsCreateModalOpen(false)}
              >
                <FaTimes />
              </button>
            </div>

            <form
              className="game-form-vertical"
              onSubmit={handleCreate}
              style={{
                display: "flex",
                flexDirection: "column",
                flex: 1,
                minHeight: 0,
                overflow: "hidden",
              }}
            >
              <div
                className="game-modal__body game-modal__body--scroll"
                style={{ flex: 1, minHeight: 0, maxHeight: "none" }}
              >
                {/* Top Section: General Info */}
                <section
                  className="game-card"
                  style={{
                    padding: "1.25rem",
                    background: "var(--game-bg-secondary)",
                    marginBottom: "1rem",
                  }}
                >
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "minmax(0, 1fr) minmax(0, 1fr)",
                      gap: "1rem",
                    }}
                  >
                    <div style={{ gridColumn: "span 1" }}>
                      <label className="game-label">Name</label>
                      <input
                        className="game-input"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        placeholder="Tournament Name"
                        required
                        style={{ width: "100%", boxSizing: "border-box" }}
                      />
                    </div>
                    <div style={{ gridColumn: "span 1" }}>
                      <label className="game-label">
                        <FaClock /> Schedule Start Time
                      </label>
                      <input
                        type="datetime-local"
                        className="game-input"
                        value={startTime}
                        onChange={(e) => setStartTime(e.target.value)}
                        required
                        style={{ width: "100%", boxSizing: "border-box" }}
                      />
                    </div>
                    <div style={{ gridColumn: "span 1" }}>
                      <label className="game-label">Countdown</label>
                      <select
                        className="game-input"
                        value={countdownSeconds}
                        onChange={(e) => setCountdownSeconds(Number(e.target.value))}
                        required
                        style={{ width: "100%", boxSizing: "border-box" }}
                      >
                        {COUNTDOWN_OPTIONS.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div style={{ gridColumn: "span 1" }}>
                      <label className="game-label">Structure Template</label>
                      <div style={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr) auto", gap: "0.75rem", minWidth: 0 }}>
                        <select
                          className="game-input"
                          value={selectedTemplate}
                          onChange={(e) => handleTemplateChange(e.target.value)}
                          required={!savedTournamentTemplateConfig}
                          style={{ width: "100%", minWidth: 0, boxSizing: "border-box" }}
                        >
                          <option value="">-- Select Template --</option>
                          {templates.map((t) => (
                            <option key={t.id} value={t.id}>
                              {t.name}
                            </option>
                          ))}
                        </select>

                        {/* New Info/View Button */}
                        <button
                          type="button"
                          className="game-icon-btn game-icon-btn--info"
                          onClick={() => {
                            if (selectedTemplate) {
                              window.open(
                                `/#/${cardroom}/tournamentsCreate?id=${selectedTemplate}&view=true`,
                                "_blank",
                              );
                            }
                          }}
                          title="View Template Structure"
                          disabled={!selectedTemplate}
                        >
                          <FaInfo />
                        </button>
                      </div>
                    </div>
                  </div>
                </section>

                <section
                  className="game-card"
                  style={{
                    padding: "1rem 1.25rem",
                    background: "rgba(250, 204, 21, 0.05)",
                    border: "1px solid rgba(250, 204, 21, 0.25)",
                    marginBottom: "1rem",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      gap: "1rem",
                      marginBottom: "1rem",
                    }}
                  >
                    <h4
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "0.5rem",
                        color: "#facc15",
                        margin: 0,
                      }}
                    >
                      <FaTrophy /> Points
                    </h4>
                    <button
                      type="button"
                      onClick={() => toggleTournamentPoints(!pointsEnabled)}
                      aria-pressed={pointsEnabled}
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: "0.6rem",
                        border: `1px solid ${pointsEnabled ? "rgba(250, 204, 21, 0.55)" : "var(--game-border)"}`,
                        background: pointsEnabled
                          ? "rgba(250, 204, 21, 0.14)"
                          : "rgba(255,255,255,0.04)",
                        color: pointsEnabled ? "#facc15" : "var(--game-text-secondary)",
                        borderRadius: 999,
                        padding: "0.45rem 0.8rem",
                        cursor: "pointer",
                        fontWeight: 800,
                      }}
                    >
                      <span
                        style={{
                          width: 32,
                          height: 18,
                          borderRadius: 999,
                          background: pointsEnabled ? "#facc15" : "#4b5563",
                          position: "relative",
                          display: "inline-block",
                        }}
                      >
                        <span
                          style={{
                            position: "absolute",
                            width: 14,
                            height: 14,
                            borderRadius: "50%",
                            background: "#111827",
                            top: 2,
                            left: pointsEnabled ? 16 : 2,
                            transition: "left 120ms ease",
                          }}
                        />
                      </span>
                      {pointsEnabled ? "On" : "Off"}
                    </button>
                  </div>

                  {pointsEnabled ? (
                    <div>
<div
  style={{
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: "1.25rem",
    alignItems: "end",
  }}
>
                      <div>
                        <label className="game-label">Preset</label>
                        <select
                          className="game-input"
                          value={pointsPresetId}
                          onChange={(e) => setPointsPresetId(e.target.value)}
                        >
                          <option value="">-- Select Preset --</option>
                          {pointPresets.map((preset) => (
                            <option key={preset.id} value={preset.id}>
                              {preset.active ? "[Active] " : ""}
                              {preset.name} ({preset.mode})
                            </option>
                          ))}
                        </select>
                      </div>

                      <div>
                        <label className="game-label">Multiplier</label>
                        <select
                          className="game-input"
                          value={pointsMultiplier}
                          onChange={(e) => setPointsMultiplier(Number(e.target.value))}
                        >
                          {POINT_MULTIPLIER_OPTIONS.map((option) => (
                            <option key={option} value={option}>
                              {option}x
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                      <div
    style={{
      border: "1px dashed rgba(244, 63, 94, 0.28)",
      borderRadius: 8,
      padding: "0.85rem 1rem",
      color: "var(--game-text-secondary)",
      background: "rgba(0,0,0,0.12)",
      marginTop: "1rem",
    }}
  >
    Points will be distributed based on selected preset and multiplier.
  </div>
                    </div>
                    
                  ) : (
                    <div
                      style={{
                        border: "1px dashed rgba(250, 204, 21, 0.28)",
                        borderRadius: 8,
                        padding: "0.85rem 1rem",
                        color: "var(--game-text-secondary)",
                        background: "rgba(0,0,0,0.12)",
                      }}
                    >
                      No points will be awarded for this tournament.
                    </div>
                  )}
                </section>

                <section
                  className="game-card"
                  style={{
                    padding: "1rem 1.25rem",
                    background: "rgba(244, 63, 94, 0.055)",
                    border: "1px solid rgba(244, 63, 94, 0.25)",
                    marginBottom: "1rem",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      gap: "1rem",
                      marginBottom: "1rem",
                    }}
                  >
                    <h4
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "0.5rem",
                        color: "#fb7185",
                        margin: 0,
                      }}
                    >
                      <FaDollarSign /> Bounty
                    </h4>
                    <button
                      type="button"
                      onClick={() => setBountyEnabled((enabled) => !enabled)}
                      aria-pressed={bountyEnabled}
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: "0.6rem",
                        border: `1px solid ${bountyEnabled ? "rgba(251, 113, 133, 0.55)" : "var(--game-border)"}`,
                        background: bountyEnabled
                          ? "rgba(244, 63, 94, 0.14)"
                          : "rgba(255,255,255,0.04)",
                        color: bountyEnabled ? "#fb7185" : "var(--game-text-secondary)",
                        borderRadius: 999,
                        padding: "0.45rem 0.8rem",
                        cursor: "pointer",
                        fontWeight: 800,
                      }}
                    >
                      <span
                        style={{
                          width: 32,
                          height: 18,
                          borderRadius: 999,
                          background: bountyEnabled ? "#fb7185" : "#4b5563",
                          position: "relative",
                          display: "inline-block",
                        }}
                      >
                        <span
                          style={{
                            position: "absolute",
                            width: 14,
                            height: 14,
                            borderRadius: "50%",
                            background: "#111827",
                            top: 2,
                            left: bountyEnabled ? 16 : 2,
                            transition: "left 120ms ease",
                          }}
                        />
                      </span>
                      {bountyEnabled ? "On" : "Off"}
                    </button>
                  </div>

{bountyEnabled ? (
  <div>
    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "1.25rem", alignItems: "end" }}>
      <div>
        <label className="game-label" style={{ display: "block", marginBottom: "0.5rem" }}>Type</label>
        <select className="game-input" value="standard" onChange={() => {}}>
          <option value="standard">Standard Bounty</option>
          <option value="progressive" disabled>Progressive - coming soon</option>
        </select>
      </div>
      <div>
        <label className="game-label">Bounty Amount</label>
        <input
          type="number"
          className="game-input"
          min="0"
          step="1"
          value={bountyAmount}
          onFocus={handleNumberFocus}
          onChange={(e) => setBountyAmount(e.target.value)}
          onWheel={preventNumberInputWheel}
          required
        />
      </div>
    </div>
  <div
    style={{
      border: "1px dashed rgba(244, 63, 94, 0.28)",
      borderRadius: 8,
      padding: "0.85rem 1rem",
      color: "var(--game-text-secondary)",
      background: "rgba(0,0,0,0.12)",
      marginTop: "1rem",
    }}
  >
    This amount is held outside the prize pool for each buy-in and rebuy.
  </div>
  </div>
) : (
  <div
    style={{
      border: "1px dashed rgba(244, 63, 94, 0.28)",
      borderRadius: 8,
      padding: "0.85rem 1rem",
      color: "var(--game-text-secondary)",
      background: "rgba(0,0,0,0.12)",
    }}
  >
    No bounties will be tracked for this tournament.
  </div>
)}
                </section>

                {/* Financial Groups */}
                {(() => {
                  const renderMoneyCard = (group) => {
                    const customTotal = getCustomAllocationTotalForAction(group.action);
                    const bountyValue = bountyEnabled && ["buyin", "rebuy"].includes(group.action)
                      ? Number(bountyAmount) || 0
                      : 0;
                    const totalToPool =
                      (Number(group.val) || 0) -
                      (Number(group.r) || 0) -
                      (Number(group.t) || 0) -
                      customTotal -
                      bountyValue;
                    const accent = group.accent || "var(--game-primary)";
                    const renderMoneyInput = ({ label, value, onChange, onBlur }) => (
                      <div className="input-with-symbol" key={label}>
                        <label
                          className="game-label"
                          style={{ fontSize: "0.75rem", opacity: 0.8 }}
                        >
                          {label}
                        </label>
                        <div style={{ position: "relative" }}>
                          <span
                            style={{
                              position: "absolute",
                              left: "10px",
                              top: "50%",
                              transform: "translateY(-50%)",
                              opacity: 0.5,
                            }}
                          >
                            $
                          </span>
                          <input
                            type="number"
                            className="game-input"
                            style={{ paddingLeft: "1.5rem" }}
                            onWheel={(e) => e.target.blur()}
                            value={value}
                            onFocus={handleNumberFocus}
                            onBlur={onBlur}
                            onChange={onChange}
                          />
                        </div>
                      </div>
                    );
                    return (
                      <div
                        key={group.label}
                        style={{
                          background: group.bg || "rgba(255,255,255,0.03)",
                          padding: "1rem",
                          borderRadius: "8px",
                          border: `1px solid ${group.border || "rgba(255,255,255,0.08)"}`,
                          borderLeft: `4px solid ${accent}`,
                        }}
                      >
                        <h4
  style={{
    color: accent,
    marginBottom: "1rem",
    fontSize: "1rem",
    fontWeight: "bold",
    textTransform: "uppercase",
    letterSpacing: "0.5px",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    width: "100%",
  }}
>
  {/* Left Side: Title & Info Icon */}
  <span style={{ display: "flex", alignItems: "center", gap: "0.4rem" }}>
    {group.label}
    {group.info && (
      <FaInfo
        title={group.info}
        style={{
          color: "var(--game-text-secondary)",
          fontSize: "0.85rem",
          cursor: "help",
        }}
      />
    )}
  </span>

  {/* Right Side: Styled Horizontal Prize Pool Container */}
  <div
    style={{
      background: group.border || "rgba(255,255,255,0.08)",
      padding: "0.35rem 0.75rem",
      borderRadius: "8px",
      border: "1px solid",
      display: "flex",
      alignItems: "center",
      gap: "0.5rem",
      fontSize: "0.7rem",
      letterSpacing: "0.5px",
    }}
  >
    <span style={{ color: "var(--game)", fontWeight: "bold" }}>
      TO PRIZE POOL:
    </span>
    <span style={{ fontSize: "1.1rem", fontWeight: "800", color: "var(--game)" }}>
      ${totalToPool}
    </span>
  </div>
</h4>

                        <div
                          style={{
                            display: "grid",
                            gridTemplateColumns: "1fr 1fr",
                            gap: "1rem",
                          }}
                        >
                          {renderMoneyInput({
                            label: "Total Amount",
                            value: group.val,
                            onBlur: () => restoreZeroOnBlur(group.val, group.set),
                            onChange: (e) => group.set(e.target.value),
                          })}
                          {renderMoneyInput({
                            label: "Rake",
                            value: group.r,
                            onBlur: () => restoreZeroOnBlur(group.r, group.setR),
                            onChange: (e) => group.setR(e.target.value),
                          })}
                          {renderMoneyInput({
                            label: "Dealer Fee",
                            value: group.t,
                            onBlur: () => restoreZeroOnBlur(group.t, group.setT),
                            onChange: (e) => group.setT(e.target.value),
                          })}
                          {customAllocations.map((allocation) =>
                            renderMoneyInput({
                              label: allocation.name,
                              value: allocation.amounts?.[group.action] ?? 0,
                              onBlur: () => {
                                if (allocation.amounts?.[group.action] === "") {
                                  updateCustomAllocationAmount(allocation.id, group.action, 0);
                                }
                              },
                              onChange: (e) =>
                                updateCustomAllocationAmount(
                                  allocation.id,
                                  group.action,
                                  e.target.value,
                                ),
                            }),
                          )}
                          {bountyValue > 0 && (
                            <div className="input-with-symbol">
                              <label className="game-label">Bounty</label>
                              <input
                                type="number"
                                className="game-input"
                                value={bountyValue}
                                readOnly
                                tabIndex={-1}
                                style={{ background: "rgba(244,63,94,0.1)", borderColor: "rgba(244,63,94,0.35)", color: "#fecdd3", cursor: "not-allowed" }}
                              />
                            </div>
                          )}
<div className="input-with-symbol">
  <label className="game-label">Add Custom Fee</label>
  <button
    type="button"
    className="game-input"
    onClick={() => setIsAddingAllocation(true)}
    title="Add Custom Fee"
    style={{
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      gap: "0.5rem",
      background: group.border || "rgba(255,255,255,0.08)",
      border: "1px solid",
      cursor: "pointer",
      textAlign: "center",
      color: "inherit",
    }}
  >
    <FaPlus /> Add Custom Fee
  </button>
</div>
                        </div>
                      </div>
                    );
                  };

                  const renderSection = (children, accent, bg) => (
                    <section
                      style={{
                        padding: "1rem",
                        background: bg,
                        border: `1px solid ${accent}`,
                        borderRadius: "8px",
                        marginBottom: "1rem",
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          gap: "1rem",
                        }}
                      >
                        {children}
                      </div>
                    </section>
                  );
                  const buyinStyle = {
                    accent: "#38bdf8",
                    border: "rgba(56,189,248,0.28)",
                    bg: "rgba(56,189,248,0.07)",
                  };
                  const rebuyStyle = {
                    accent: "#f59e0b",
                    border: "rgba(245,158,11,0.28)",
                    bg: "rgba(245,158,11,0.07)",
                  };
                  const addonStyle = {
                    accent: "#22c55e",
                    border: "rgba(34,197,94,0.28)",
                    bg: "rgba(34,197,94,0.07)",
                  };
                  const {
                    showBonus,
                    showBuyinAddon,
                    showRebuyAddon,
                    showTournamentAddon,
                  } = getTemplateFeatureVisibility();

                  return (
                    <>
                      {renderSection(
                        [
                          renderMoneyCard({
                            label: "Buy-In",
                            action: "buyin",
                            val: buyin,
                            set: setBuyin,
                            r: rake,
                            setR: setRake,
                            t: tips,
                            setT: setTips,
                            ...buyinStyle,
                          }),
                          showBuyinAddon &&
                          renderMoneyCard({
                            label: "Buy-in Dealer Add-on",
                            action: "buyin_addon",
                            val: buyinAddonCost,
                            set: setBuyinAddonCost,
                            r: buyinAddonRake,
                            setR: setBuyinAddonRake,
                            t: buyinAddonTips,
                            setT: setBuyinAddonTips,
                            ...buyinStyle,
                          }),
                          showBonus &&
                         renderMoneyCard({
                            label: "Buy-in Bonus",
                            action: "bonus",
                            val: bonusBuyin,
                            set: setBonusBuyin,
                            r: bonusRake,
                            setR: setBonusRake,
                            t: bonusTips,
                            setT: setBonusTips,
                            info: "Usually cost $0 and used for promotional chips, like registering through Facebook or another promo.",
                            ...buyinStyle,
                          }),
                        ].filter(Boolean),
                        buyinStyle.border,
                        "rgba(56,189,248,0.035)",
                      )}

                      {renderSection(
                        [
                          renderMoneyCard({
                            label: "Rebuy",
                            action: "rebuy",
                            val: rebuyCost,
                            set: setRebuyCost,
                            r: rebuyRake,
                            setR: setRebuyRake,
                            t: rebuyTips,
                            setT: setRebuyTips,
                            ...rebuyStyle,
                          }),
                          showRebuyAddon &&
                          renderMoneyCard({
                            label: "Rebuy Dealer Add-on",
                            action: "rebuy_addon",
                            val: rebuyAddOnCost,
                            set: setRebuyAddOnCost,
                            r: rebuyAddOnRake,
                            setR: setRebuyAddOnRake,
                            t: rebuyAddOnTips,
                            setT: setRebuyAddOnTips,
                            ...rebuyStyle,
                          }),
                        ].filter(Boolean),
                        rebuyStyle.border,
                        "rgba(245,158,11,0.035)",
                      )}

                      {showTournamentAddon && renderSection(
                        [
                          renderMoneyCard({
                            label: "Tournament Add-on",
                            action: "addon",
                            val: addonCost,
                            set: setAddonCost,
                            r: addonRake,
                            setR: setAddonRake,
                            t: addonTips,
                            setT: setAddonTips,
                            ...addonStyle,
                          }),
                        ],
                        addonStyle.border,
                        "rgba(34,197,94,0.035)",
                      )}
                    </>
                  );
                })()}

              </div>

                {/* Action Buttons */}
                <div
                  className="game-modal__footer"
                  style={{
                    flexShrink: 0,
                  }}
                >
                  <button
                    type="button"
                    className="game-btn game-btn--secondary"
                    style={{ flex: 1 }}
                    onClick={() => setIsCreateModalOpen(false)}
                  >
                    Cancel
                  </button>
                  {createModalMode === "create" && (
                    <button
                      type="button"
                      className="game-btn game-btn--secondary"
                      style={{ flex: 1 }}
                      disabled={isSubmitting || !isFormValid()}
                      onClick={handleSaveTournamentSetup}
                    >
                      <FaPlus /> Save Tournament
                    </button>
                  )}
                  <button
                    type="submit"
                    className="game-btn game-btn--primary"
                    style={{ flex: 1 }}
                    disabled={isSubmitting || !isFormValid()}
                  >
                    <FaCalendarAlt />{" "}
                    {isSubmitting
                      ? "Saving..."
                      : createModalMode === "create"
                        ? "Schedule Tournament"
                        : "Save Tournament"}
                  </button>
                </div>
              </form>
          </div>
        </>
      )}

      {/* Register Players Modal */}
      {registrationModal.isOpen && (
        <>
          <div
            className="game-modal-overlay"
            onClick={() =>
              setRegistrationModal({
                isOpen: false,
                tournament: null,
                loading: false,
              })
            }
          />
          <div
            className="game-modal game-modal--large"
            style={{ maxWidth: "650px" }}
          >
            <div className="game-modal__header">
              <h3 className="game-modal__title">
                {registrationModal.tournament?.name || "Tournament"}{" "}
                Registration
              </h3>
              <button
                className="game-icon-btn"
                onClick={() =>
                  setRegistrationModal({
                    isOpen: false,
                    tournament: null,
                    loading: false,
                  })
                }
              >
                <FaTimes />
              </button>
            </div>

            <div
              className="game-modal__body game-modal__body--scroll"
              style={{ maxHeight: "75vh" }}
            >
              {registrationModal.loading ? (
                <p className="game-empty">Loading registration...</p>
              ) : (
                <>
                  {(() => {
                    const tournamentPlayerIds = new Set(
                      (registrationModal.tournament?.players || []).map((p) =>
                        Number(p.player_id),
                      ),
                    );
                    const options = registrationPlayers
                      .filter((p) => !tournamentPlayerIds.has(Number(p.id)))
                      .map((p) => ({ label: p.name, value: p.id }));

                    return (
                      <section
                        className="game-card"
                        style={{
                          padding: "1.25rem",
                          background: "var(--game-bg-secondary)",
                          marginBottom: "1rem",
                        }}
                      >
                        <label className="game-label">
                          Add Registered Players
                        </label>
                        <Select
                          isMulti
                          isSearchable
                          options={options}
                          value={selectedRegistrationPlayers}
                          onChange={(value) =>
                            setSelectedRegistrationPlayers(value || [])
                          }
                          placeholder="Search and select players..."
                          className="game-select-container"
                          classNamePrefix="game-select"
                          styles={selectStyles}
                        />
                        <button
                          type="button"
                          className="game-btn game-btn--primary"
                          style={{
                            width: "100%",
                            marginTop: "1rem",
                            justifyContent: "center",
                          }}
                          disabled={
                            isSubmitting ||
                            selectedRegistrationPlayers.length === 0
                          }
                          onClick={confirmBulkRegisterPlayers}
                        >
                          Confirm {selectedRegistrationPlayers.length} Player
                          {selectedRegistrationPlayers.length !== 1 ? "s" : ""}
                        </button>
                      </section>
                    );
                  })()}

                  <section
                    className="game-card"
                    style={{
                      padding: "1.25rem",
                      background: "var(--game-bg-secondary)",
                    }}
                  >
                    <h4
                      style={{
                        margin: "0 0 1rem",
                        color: "var(--game-text-primary)",
                      }}
                    >
                      Registered ({registeredPlayers.length})
                    </h4>
                    {registeredPlayers.length === 0 ? (
                      <p className="game-empty" style={{ margin: 0 }}>
                        No registered players yet.
                      </p>
                    ) : (
                      <div
                        className="game-list"
                        style={{
                          display: "grid",
                          gridTemplateColumns:
                            "repeat(auto-fit, minmax(240px, 1fr))",
                          gap: "0.75rem",
                        }}
                      >
                        {registeredPlayers.map((p) => (
                          <div key={p.player_id} className="game-list-item">
                            <span
                              className="game-list-item__title"
                              style={{ margin: 0 }}
                            >
                              {p.name}
                            </span>
                            <button
                              type="button"
                              className="game-icon-btn game-icon-btn--danger"
                              title="Remove Registration"
                              disabled={isSubmitting}
                              onClick={() =>
                                removeRegisteredPlayer(p.player_id)
                              }
                            >
                              <FaTrash />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </section>
                </>
              )}
            </div>
          </div>
        </>
      )}

      {/* Cancel Tournament Confirmation */}
      {deleteTournamentModal.isOpen && (
        <>
          <div
            className="game-modal-overlay"
            onClick={() =>
              setDeleteTournamentModal({ isOpen: false, tournament: null })
            }
          />
          <div className="game-modal">
            <div className="game-modal__header">
              <h3 className="game-modal__title">Cancel Tournament</h3>
              <button
                className="game-icon-btn"
                onClick={() =>
                  setDeleteTournamentModal({ isOpen: false, tournament: null })
                }
              >
                <FaTimes />
              </button>
            </div>
            <div className="game-modal__body">
              <p
                style={{ color: "var(--game-text-secondary)", lineHeight: 1.5 }}
              >
                Delete{" "}
                <strong style={{ color: "#fff" }}>
                  {deleteTournamentModal.tournament?.name}
                </strong>
                ? This will remove its registration list and tournament record.
              </p>
              <div
                style={{ display: "flex", gap: "1rem", marginTop: "1.5rem" }}
              >
                <button
                  className="game-btn game-btn--secondary"
                  style={{ flex: 1 }}
                  onClick={() =>
                    setDeleteTournamentModal({
                      isOpen: false,
                      tournament: null,
                    })
                  }
                >
                  Keep Tournament
                </button>
                <button
                  className="game-btn game-btn--danger"
                  style={{ flex: 1 }}
                  disabled={isSubmitting}
                  onClick={confirmDeleteTournament}
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Delete Saved Tournament Confirmation */}
      {deleteSavedTournamentModal.isOpen && (
        <>
          <div
            className="game-modal-overlay"
            onClick={() =>
              setDeleteSavedTournamentModal({
                isOpen: false,
                savedTournament: null,
              })
            }
          />
          <div className="game-modal">
            <div className="game-modal__header">
              <h3 className="game-modal__title">Delete Saved Tournament</h3>
              <button
                className="game-icon-btn"
                onClick={() =>
                  setDeleteSavedTournamentModal({
                    isOpen: false,
                    savedTournament: null,
                  })
                }
              >
                <FaTimes />
              </button>
            </div>
            <div className="game-modal__body">
              <p
                style={{ color: "var(--game-text-secondary)", lineHeight: 1.5 }}
              >
                Delete saved tournament{" "}
                <strong style={{ color: "#fff" }}>
                  {deleteSavedTournamentModal.savedTournament?.name}
                </strong>
                ?
              </p>
              <div
                style={{ display: "flex", gap: "1rem", marginTop: "1.5rem" }}
              >
                <button
                  className="game-btn game-btn--secondary"
                  style={{ flex: 1 }}
                  onClick={() =>
                    setDeleteSavedTournamentModal({
                      isOpen: false,
                      savedTournament: null,
                    })
                  }
                >
                  Keep Saved Tournament
                </button>
                <button
                  className="game-btn game-btn--danger"
                  style={{ flex: 1 }}
                  disabled={isSubmitting}
                  onClick={confirmDeleteSavedTournament}
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Delete Template Confirmation */}
      {deleteTemplateModal.isOpen && (
        <>
          <div
            className="game-modal-overlay"
            onClick={() =>
              setDeleteTemplateModal({ isOpen: false, template: null })
            }
          />
          <div className="game-modal">
            <div className="game-modal__header">
              <h3 className="game-modal__title">Delete Template</h3>
              <button
                className="game-icon-btn"
                onClick={() =>
                  setDeleteTemplateModal({ isOpen: false, template: null })
                }
              >
                <FaTimes />
              </button>
            </div>
            <div className="game-modal__body">
              <p
                style={{ color: "var(--game-text-secondary)", lineHeight: 1.5 }}
              >
                Delete template{" "}
                <strong style={{ color: "#fff" }}>
                  {deleteTemplateModal.template?.name}
                </strong>
                ?
              </p>
              <div
                style={{ display: "flex", gap: "1rem", marginTop: "1.5rem" }}
              >
                <button
                  className="game-btn game-btn--secondary"
                  style={{ flex: 1 }}
                  onClick={() =>
                    setDeleteTemplateModal({ isOpen: false, template: null })
                  }
                >
                  Keep Template
                </button>
                <button
                  className="game-btn game-btn--danger"
                  style={{ flex: 1 }}
                  disabled={isSubmitting}
                  onClick={confirmDeleteTemplate}
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Wallet Payment Modal */}
      {payingWallet && (
        <>
          <div
            className="game-modal-overlay"
            onClick={() => setPayingWallet(null)}
          />
          <div className="game-modal game-modal--transaction">
            <div className="game-modal__header">
              <h3 className="game-modal__title">
                Payment: {payingWallet.name}
              </h3>
              {payingWallet.digital !== 0 && (
                <span
                  className={
                    payingWallet.digital > 0
                      ? "game-badge game-badge--debt"
                      : "game-badge game-badge--credit"
                  }
                >
                  {payingWallet.digital > 0
                    ? `Owes $${Math.abs(payingWallet.digital)}`
                    : `Owed $${Math.abs(payingWallet.digital)}`}
                </span>
              )}
            </div>

            <div className="game-modal__body">
              <form
                className="game-form-vertical"
                onSubmit={handleWalletPayment}
              >
                {/* Direction Toggle */}
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr",
                    gap: "0.5rem",
                    marginBottom: "0.5rem",
                  }}
                >
                  <button
                    type="button"
                    className={`game-btn ${
                      payDirection === "to_wallet"
                        ? "game-btn--primary"
                        : "game-btn--secondary"
                    }`}
                    onClick={() => setPayDirection("to_wallet")}
                  >
                    House Wallet
                  </button>
                  <button
                    type="button"
                    className={`game-btn ${
                      payDirection === "from_wallet"
                        ? "game-btn--primary"
                        : "game-btn--secondary"
                    }`}
                    onClick={() => setPayDirection("from_wallet")}
                  >
                    Wallet House
                  </button>
                </div>

                {/* Visual Direction Indicator */}
                <div
                  style={{
                    padding: "1rem",
                    background: "var(--game-bg-tertiary)",
                    borderRadius: "var(--game-radius)",
                    marginBottom: "1rem",
                    textAlign: "center",
                    fontWeight: 600,
                    color:
                      payDirection === "to_wallet"
                        ? "var(--game-success)"
                        : "var(--game-danger)",
                  }}
                >
                  {payDirection === "to_wallet" ? (
                    <>House paying {payingWallet.name}</>
                  ) : (
                    <>{payingWallet.name} paying House</>
                  )}
                </div>

                {/* Amount */}
                <input
                  type="number"
                  className="game-input"
                  step="any"
                  min="0"
                  placeholder="Amount"
                  value={payWalletAmount}
                  onFocus={handleNumberFocus}
                  onChange={(e) => setPayWalletAmount(e.target.value)}
                  required
                />

                {/* Notes */}
                <textarea
                  className="game-input"
                  placeholder="Notes (optional)"
                  value={payWalletNotes}
                  onChange={(e) => setPayWalletNotes(e.target.value)}
                  rows={3}
                  style={{ resize: "vertical", minHeight: "80px" }}
                />

                {/* Summary */}
                {payWalletAmount && (
                  <div
                    style={{
                      padding: "0.75rem",
                      background: "var(--game-bg-tertiary)",
                      borderRadius: "var(--game-radius)",
                      fontSize: "0.875rem",
                      color: "var(--game-text-secondary)",
                    }}
                  >
                    {payDirection === "to_wallet" ? (
                      <>
                        New balance:{" "}
                        <strong style={{ color: "var(--game-text-primary)" }}>
                          {payingWallet.digital + parseFloat(payWalletAmount) >
                          0
                            ? "Owes "
                            : `Owed `}
                          $
                          {Math.abs(
                            payingWallet.digital + parseFloat(payWalletAmount),
                          )}
                        </strong>
                      </>
                    ) : (
                      <>
                        New balance:{" "}
                        <strong style={{ color: "var(--game-text-primary)" }}>
                          {payingWallet.digital - parseFloat(payWalletAmount) >
                          0
                            ? "Owes "
                            : `Owed `}
                          $
                          {Math.abs(
                            payingWallet.digital - parseFloat(payWalletAmount),
                          )}
                        </strong>
                      </>
                    )}
                  </div>
                )}

                {/* Actions */}
                <div
                  className="game-modal__footer"
                  style={{
                    marginTop: "1.5rem",
                    display: "flex",
                    justifyContent: "flex-end",
                    gap: "1rem",
                  }}
                >
                  <button
                    type="button"
                    className="game-btn game-btn--ghost"
                    onClick={() => setPayingWallet(null)}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="game-btn game-btn--primary"
                    disabled={
                      isSubmitting ||
                      !payWalletAmount ||
                      parseFloat(payWalletAmount) <= 0
                    }
                  >
                    Submit
                  </button>
                </div>
              </form>
            </div>
          </div>
        </>
      )}

{/* Add Custom Fee Modal */}
{isAddingAllocation && (
  <>
    <div
      className="game-modal-overlay"
      onClick={() => { setIsAddingAllocation(false); setNewAllocationName(""); }}
    />
    <div className="game-modal">
      <div className="game-modal__header">
        <h3 className="game-modal__title">Add Custom Fee</h3>
        <button
          className="game-icon-btn"
          onClick={() => { setIsAddingAllocation(false); setNewAllocationName(""); }}
        >
          <FaTimes />
        </button>
      </div>
      <div className="game-modal__body">
        <label className="game-label">Fee Name</label>
        <input
          autoFocus
          type="text"
          className="game-input"
          placeholder="e.g. Bounty, Jackpot Drop..."
          value={newAllocationName}
          onChange={(e) => setNewAllocationName(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") { e.preventDefault(); handleAddCustomAllocation(); }
            if (e.key === "Escape") { setIsAddingAllocation(false); setNewAllocationName(""); }
          }}
          style={{ marginBottom: "1.5rem" }}
        />
        <div style={{ display: "flex", gap: "1rem" }}>
          <button
            type="button"
            className="game-btn game-btn--secondary"
            style={{ flex: 1 }}
            onClick={() => { setIsAddingAllocation(false); setNewAllocationName(""); }}
          >
            Cancel
          </button>
          <button
            type="button"
            className="game-btn game-btn--primary"
            style={{ flex: 1 }}
            disabled={!newAllocationName.trim()}
            onClick={handleAddCustomAllocation}
          >
            Add Fee
          </button>
        </div>
      </div>
    </div>
  </>
)}


      {/* Wallet Transactions Modal */}
      {selectedWalletId && (
        <>
          <div
            className="game-modal-overlay"
            onClick={() => setSelectedWalletId(null)}
          />
          <div className="game-modal game-modal--large">
            <div className="game-modal__header">
              <h3 className="game-modal__title">
                {tournamentWallets.find((w) => w.id === selectedWalletId)?.name}{" "}
                History
              </h3>
              <button
                className="game-icon-btn"
                onClick={() => setSelectedWalletId(null)}
              >
                <FaTimes />
              </button>
            </div>

            <div className="game-modal__body game-modal__body--scroll">
              {selectedWalletTransactions.length === 0 ? (
                <p className="game-empty">No transactions found</p>
              ) : (
                <div className="game-transaction-list">
                  {selectedWalletTransactions.map((t) => {
                    const isManual = t.source === "wallet_payment";
                    const amt = parseFloat(t.amount).toFixed(0);
                    const isToWallet = isManual && t.amount > 0;

                    return (
                      <div
                        key={t.id}
                        className="game-transaction-item"
                        style={
                          isManual
                            ? {
                                background: isToWallet
                                  ? "rgba(72, 187, 120, 0.1)"
                                  : "rgba(245, 101, 101, 0.1)",
                                border: isToWallet
                                  ? "1px solid rgba(72, 187, 120, 0.3)"
                                  : "1px solid rgba(245, 101, 101, 0.3)",
                              }
                            : {}
                        }
                      >
                        <span className="game-transaction-time">
                          {new Date(t.completed_at).toLocaleTimeString(
                            undefined,
                            { hour: "numeric", minute: "2-digit" },
                          )}
                        </span>

                        <span className="game-transaction-amount">
                          ${Math.abs(amt)}
                        </span>

                        {(t.entity_name || isManual) && (
                          <strong className="game-transaction-player">
                            {isManual ? "Manual Payment" : t.entity_name}
                          </strong>
                        )}

                        <span className="game-transaction-type">
                          {!isManual &&
                            (t.source ? t.source.toUpperCase() : "UNKNOWN")}
                          {isManual &&
                            (isToWallet ? "House â†’ Wallet" : "Wallet â†’ House")}
                        </span>

                        <span className="game-transaction-table">
                          {t.table_name ? t.table_name : null}
                        </span>

                        {isManual && (
                          <span
                            className={`game-badge ${isToWallet ? "game-badge--credit" : "game-badge--debt"}`}
                            style={{ marginLeft: "auto" }}
                          >
                            {isToWallet ? "Received" : "Sent"}
                          </span>
                        )}

                        <span
                          className="game-transaction-date"
                          style={{ marginLeft: isManual ? "1rem" : "auto" }}
                        >
                          {new Date(t.completed_at).toLocaleDateString(
                            undefined,
                            {
                              month: "numeric",
                              day: "numeric",
                            },
                          )}
                        </span>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </>
      )}

      {/* SESSION COSTS MODAL */}
      {isSessionCostsOpen && (
        <>
          <div
            className="game-modal-overlay"
            onClick={() => setIsSessionCostsOpen(false)}
          />
          <div className="game-modal game-modal--large">
            <div className="game-modal__header">
              <h3 className="game-modal__title">Session Costs</h3>
              <button
                className="game-icon-btn"
                onClick={() => setIsSessionCostsOpen(false)}
              >
                <FaTimes />
              </button>
            </div>
            <div className="game-modal__body game-modal__body--scroll">
              {sessionCostsData.length === 0 ? (
                <p className="game-empty">
                  No costs recorded for this session.
                </p>
              ) : (
                <div className="game-transaction-list">
                  {sessionCostsData.map((c) => {
                    return (
                      <div key={c.id} className="game-transaction-item">
                        <span className="game-transaction-time">
                          {new Date(c.completed_at).toLocaleTimeString(
                            undefined,
                            {
                              hour: "numeric",
                              minute: "2-digit",
                            },
                          )}
                        </span>

                        <span className="game-transaction-amount game-text-danger">
                          ${c.amount}
                        </span>

                        <span
                          className="game-transaction-type"
                          style={{ marginLeft: "1rem" }}
                        >
                          {c.tournament_name || "Unknown Tournament"}
                        </span>

                        <span
                          className="game-transaction-method"
                          style={{ marginLeft: "1rem" }}
                        >
                          Notes: {c.notes || "None"}
                        </span>

                        <span
                          className="game-transaction-method"
                          style={{ marginLeft: "1rem" }}
                        >
                          {c.wallet_name ? `Wallet (${c.wallet_name})` : "Cash"}
                        </span>

                        <span
                          className="game-transaction-date"
                          style={{ marginLeft: "auto" }}
                        >
                          {new Date(c.completed_at).toLocaleDateString(
                            undefined,
                            {
                              month: "numeric",
                              day: "numeric",
                            },
                          )}
                        </span>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </>
      )}

      {/* CASH BAG HISTORY MODAL */}
      {isCashBagHistoryOpen && (
        <>
          <div
            className="game-modal-overlay"
            onClick={() => setIsCashBagHistoryOpen(false)}
          />
          <div className="game-modal game-modal--large">
            <div className="game-modal__header">
              <h3 className="game-modal__title">Tournament Cash Bag History</h3>
              <button
                className="game-icon-btn"
                onClick={() => setIsCashBagHistoryOpen(false)}
              >
                <FaTimes />
              </button>
            </div>
            <div className="game-modal__body game-modal__body--scroll">
              {cashBagTransactions.length === 0 ? (
                <p className="game-empty">No cash bag transactions found.</p>
              ) : (
                <div className="game-transaction-list">
                  {cashBagTransactions.map((t) => {
                    return (
                      <div
                        key={t.id + t.type}
                        className="game-transaction-item"
                      >
                        <span className="game-transaction-time">
                          {new Date(t.completed_at).toLocaleTimeString(
                            undefined,
                            {
                              hour: "numeric",
                              minute: "2-digit",
                            },
                          )}
                        </span>

                        <span
                          className={`game-transaction-amount ${
                            t.type === "cashout"
                              ? "game-text-danger"
                              : t.amount > 0 && t.type !== "cost"
                                ? "game-text-success"
                                : "game-text-warning"
                          }`}
                        >
                          ${t.amount}
                        </span>

                        <span
                          className="game-transaction-type"
                          style={{ marginLeft: "1rem", marginRight: "1rem" }}
                        >
                          {t.type.toUpperCase()}
                        </span>

                        {t.table_name && (
                          <span
                            className="game-transaction-table"
                            style={{ marginRight: "1rem" }}
                          >
                            {t.table_name}
                          </span>
                        )}

                        {t.name && (
                          <span className="game-transaction-method">
                            {t.name}
                          </span>
                        )}

                        <span
                          className="game-transaction-date"
                          style={{ marginLeft: "auto" }}
                        >
                          {new Date(t.completed_at).toLocaleDateString(
                            undefined,
                            {
                              month: "numeric",
                              day: "numeric",
                            },
                          )}
                        </span>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </>
      )}

      {/* HISTORY MODAL */}
      {historyTournament && (
        <>
          <div
            className="game-modal-overlay"
            onClick={() => setHistoryTournament(null)}
          />
          <div className="game-modal game-modal--large">
            <div className="game-modal__header">
              <h3 className="game-modal__title">
                {historyTournament.name}
              </h3>
              <div style={{ display: "flex", gap: "0.4rem", marginLeft: "auto", marginRight: "1rem" }}>
                {[
                  { key: "placement", label: "Placement" },
                  { key: "activity", label: "Activity" },
                  { key: "stats", label: "Stats" },
                  { key: "costs", label: "Costs" },
                ].map((tab) => (
                  <button
                    key={tab.key}
                    type="button"
                    className={`game-btn game-btn--sm ${historyTab === tab.key ? "game-btn--primary" : "game-btn--secondary"}`}
                    onClick={() => setHistoryTab(tab.key)}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>
              <button
                className="game-icon-btn"
                onClick={() => setHistoryTournament(null)}
              >
                <FaTimes />
              </button>
            </div>

            <div className="game-modal__body game-modal__body--scroll">
              {historyTab === "placement" && (
                historyPlacements.length === 0 ? (
                  <p className="game-empty">No placements recorded.</p>
                ) : (
                  <div className="game-transaction-list">
                    {historyPlacements.map((record) => (
                      <div key={record.id || `${record.player_id}-${record.place}`} className="game-transaction-item">
                        <span className="game-transaction-time">#{record.place}</span>
                        <span className="game-transaction-type">{record.name || "Unknown"}</span>
                        <span className="game-transaction-method">{Number(record.points) || 0} pts</span>
                        <span className="game-transaction-amount">{money(record.cash)}</span>
                      </div>
                    ))}
                  </div>
                )
              )}

              {historyTab === "activity" && (
                <TournamentActivityList
                  transactions={historyTransactions}
                  emptyText="No transactions found."
                />
              )}

              {historyTab === "stats" && (
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))", gap: "0.75rem" }}>
                  {[
                    ["Prize Pool", money(historyTournament.prizepool)],
                    ["Rake", money(historyTournament.total_rake)],
                    ["Tips", money(historyTournament.total_tips)],
                    ["Costs", money(historyTournament.costs)],
                    ["Ending Cash", money(historyTournament.ending_cash)],
                    ["Free Entries", historyTournament.free_entries || 0],
                    ["Add-ons", historyTournament.addons_num || 0],
                    ["Unique Entries", historyTournament.buyins_num || 0],
                    ["Rebuys", historyTournament.rebuys_num || 0],
                    ["Total Entries", historyTournament.total_entries || 0],
                    ["Profit", money(historyTournament.profit)],
                  ].map(([label, value]) => (
                    <div key={label} style={{ background: "var(--game-bg-tertiary)", borderRadius: "8px", padding: "1rem", textAlign: "center" }}>
                      <div style={{ fontSize: "0.75rem", color: "var(--game-text-secondary)", fontWeight: 700, textTransform: "uppercase", marginBottom: "0.5rem" }}>{label}</div>
                      <div style={{ fontSize: "1.25rem", fontWeight: 800 }}>{value}</div>
                    </div>
                  ))}
                  {historyCustomFees.map((allocation) => (
                    <div key={allocation.id || allocation.name} style={{ background: "var(--game-bg-tertiary)", borderRadius: "8px", padding: "1rem", textAlign: "center" }}>
                      <div style={{ fontSize: "0.75rem", color: "var(--game-text-secondary)", fontWeight: 700, textTransform: "uppercase", marginBottom: "0.5rem" }}>{allocation.name}</div>
                      <div style={{ fontSize: "1.25rem", fontWeight: 800 }}>{money(allocation.total)}</div>
                    </div>
                  ))}
                </div>
              )}

              {historyTab === "costs" && (
                historyCosts.length === 0 ? (
                  <p className="game-empty">No costs recorded yet.</p>
                ) : (
                  <div className="game-transaction-list">
                    {historyCosts.map((cost) => (
                      <div key={cost.id} className="game-transaction-item">
                        <span className="game-transaction-time">
                          {new Date(cost.completed_at).toLocaleTimeString(
                            undefined,
                            { hour: "numeric", minute: "2-digit" },
                          )}
                        </span>
                        <span className="game-transaction-amount">{money(cost.amount)}</span>
                        <span className="game-transaction-type">{cost.notes || "Cost"}</span>
                        <span className="game-transaction-method">
                          {cost.wallet_name ? `Wallet: ${cost.wallet_name}` : "Cash"}
                        </span>
                        <span className="game-transaction-date">
                          {new Date(cost.completed_at).toLocaleDateString(
                            undefined,
                            { month: "numeric", day: "numeric" },
                          )}
                        </span>
                      </div>
                    ))}
                  </div>
                )
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

